import { useState } from 'react';
import { RecordConversations } from '../components/messaging/RecordConversations';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Mail, Check, Archive, RefreshCw, ArrowRightCircle } from 'lucide-react';
import type { Inquiry, InquiryStatus } from '@peles/shared';
import { getInquiries, updateInquiryStatus, convertInquiryToEvent } from '../api/inquiries';
import { fetchThreadCounts } from '../api/notifications';

const STATUS_COLORS: Record<InquiryStatus, string> = {
  NEW: 'bg-blue-100 text-blue-800',
  CONTACTED: 'bg-yellow-100 text-yellow-800',
  ARCHIVED: 'bg-gray-100 text-gray-800',
};

const STATUS_FILTERS: { label: string; value: string }[] = [
  { label: 'All', value: '' },
  { label: 'New', value: 'NEW' },
  { label: 'Contacted', value: 'CONTACTED' },
  { label: 'Archived', value: 'ARCHIVED' },
];

/**
 * ⚠️ **The conversation goes on the card, not behind a detail page.** There is
 * no per-inquiry route — enquiries are worked from this list — so a thread
 * reachable only from the notification bell would be a feature producers had to
 * be told about. It sits with the actions they already use.
 */
export function InquiriesPage() {
  const [statusFilter, setStatusFilter] = useState('');
  const [convertedMessage, setConvertedMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: inquiries = [], isLoading } = useQuery<Inquiry[]>({
    queryKey: ['inquiries', statusFilter],
    queryFn: () => getInquiries(statusFilter ? { status: statusFilter } : undefined),
  });

  /*
   * One request for every card's badge, rather than one per card.
   *
   * `<RecordConversations>` without a `count` renders the package's
   * self-fetching button, which is right on a detail page and wrong here: this
   * list is unpaginated, so it would put a request on the wire per inquiry.
   * `fetchThreadCounts` chunks to the endpoint's 200-record limit rather than
   * truncating, so a long list costs a few requests instead of reporting every
   * inquiry past the 200th as having no conversation.
   *
   * Until it answers the cards are given `null` — "the page owns this badge and
   * does not know the number yet" — because rendering an unfetched count as
   * `0` claims a conversation does not exist.
   */
  const inquiryIds = inquiries.map((inquiry) => inquiry.id);
  const { data: threadCounts } = useQuery({
    queryKey: ['inquiry-thread-counts', inquiryIds],
    queryFn: () => fetchThreadCounts('INQUIRY', inquiryIds),
    enabled: inquiryIds.length > 0,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: InquiryStatus }) =>
      updateInquiryStatus(id, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inquiries'] });
    },
  });

  const convertMutation = useMutation({
    mutationFn: (id: string) => convertInquiryToEvent(id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['inquiries'] });
      setConvertedMessage(
        `Event "${result.event.name}" created and user ${result.user.email} assigned.`,
      );
      setTimeout(() => setConvertedMessage(null), 5000);
    },
  });

  function formatDate(date: Date | string): string {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Mail className="h-6 w-6 text-gray-700" />
          <h1 className="text-2xl font-bold text-gray-900">Inquiries</h1>
        </div>
        <button
          onClick={() => queryClient.invalidateQueries({ queryKey: ['inquiries'] })}
          className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {convertedMessage && (
        <div
          className="mb-4 px-4 py-3 bg-green-50 border border-green-200 text-green-800 text-sm rounded-lg"
          data-testid="convert-success"
        >
          {convertedMessage}
        </div>
      )}

      <div className="flex gap-2 mb-6">
        {STATUS_FILTERS.map((filter) => (
          <button
            key={filter.value}
            onClick={() => setStatusFilter(filter.value)}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
              statusFilter === filter.value
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-gray-500">Loading inquiries...</div>
      ) : inquiries.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No inquiries found.</div>
      ) : (
        <div className="space-y-4">
          {inquiries.map((inquiry) => (
            <div
              key={inquiry.id}
              className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-sm transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-gray-900">{inquiry.name}</h3>
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_COLORS[inquiry.status]}`}
                    >
                      {inquiry.status}
                    </span>
                  </div>
                  <div className="mt-1 text-sm text-gray-600">
                    <a href={`mailto:${inquiry.email}`} className="hover:underline">
                      {inquiry.email}
                    </a>
                    {inquiry.phone && <span className="ml-3">{inquiry.phone}</span>}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-4 text-sm text-gray-500">
                    {inquiry.eventType && (
                      <span>
                        <strong>Type:</strong> {inquiry.eventType}
                      </span>
                    )}
                    {inquiry.estimatedDate && (
                      <span>
                        <strong>Date:</strong> {inquiry.estimatedDate}
                      </span>
                    )}
                    {inquiry.estimatedGuestCount && (
                      <span>
                        <strong>Guests:</strong> {inquiry.estimatedGuestCount}
                      </span>
                    )}
                  </div>
                  {inquiry.message && (
                    <p className="mt-3 text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                      {inquiry.message}
                    </p>
                  )}
                  <div className="mt-3">
                    <RecordConversations
                      entityType="INQUIRY"
                      entityId={inquiry.id}
                      label="Conversation"
                      count={
                        threadCounts ? (threadCounts[inquiry.id] ?? { total: 0, unread: 0 }) : null
                      }
                    />
                  </div>
                  <div className="mt-3 text-xs text-gray-400">
                    Received {formatDate(inquiry.createdAt)}
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  {inquiry.status === 'NEW' && (
                    <button
                      onClick={() => convertMutation.mutate(inquiry.id)}
                      disabled={convertMutation.isPending}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-colors"
                      title="Convert to event"
                    >
                      <ArrowRightCircle className="h-4 w-4" />
                      Convert
                    </button>
                  )}
                  {inquiry.status !== 'CONTACTED' && (
                    <button
                      onClick={() => updateMutation.mutate({ id: inquiry.id, status: 'CONTACTED' })}
                      disabled={updateMutation.isPending}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm bg-yellow-50 text-yellow-700 rounded-lg hover:bg-yellow-100 transition-colors"
                      title="Mark as contacted"
                    >
                      <Check className="h-4 w-4" />
                      Contacted
                    </button>
                  )}
                  {inquiry.status !== 'ARCHIVED' && (
                    <button
                      onClick={() => updateMutation.mutate({ id: inquiry.id, status: 'ARCHIVED' })}
                      disabled={updateMutation.isPending}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                      title="Archive"
                    >
                      <Archive className="h-4 w-4" />
                      Archive
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
